import clsx from 'clsx';
import { randomNumber } from '../../lib/constants';
import { RadioButton } from '../RadioButton';
import { ChangeEvent } from 'react';

interface Props extends BaseProps {
  direction?: FlexDirection;
  disabled?: boolean;
  fullWidth?: boolean;
  label?: string;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  options: RadioButtonOption[];
  padding?: PaddingVariant;
  radioLabelDirection?: FlexDirection;
  required?: boolean;
  selection: string;
  spacing?: FlexJustify;
  showBorder?: boolean;
}

export const RadioGroup = (props: Props) => {
  const {
    direction = 'row',
    disabled = false,
    fullWidth = false,
    radioLabelDirection = 'row',
    spacing = 'center',
    label = null,
    onChange,
    options,
    padding = 'md',
    required = false,
    selection,
    showBorder = false,
    ...rest
  } = props;
  const sortOrder = (a: RadioButtonOption, b: RadioButtonOption) =>
    a.order < b.order ? -1 : 1;
  const width = fullWidth ? 'fullWidth' : 'unset';
  const groupId = randomNumber();
  const classes = useStyles();

  const renderLabel = () => {
    if (!label) return;
    return (
      <label
        id={`radio-group-label-${groupId}`}
        htmlFor={`radio-group-${groupId}`}
        aria-required={required}
        aria-label={label}
        className={clsx(classes.label.default, {
          [classes.label.group]: showBorder,
        })}
      >
        {required ? `${label} *` : `${label}:`}
      </label>
    );
  };

  return (
    <div
      id={`radio-group-${groupId}`}
      className={clsx(
        classes.container.default,
        classes.container.direction[direction],
        classes.container.justify[spacing],
        classes.container.padding[padding],
        classes.width[width],
        { [classes.showBorder]: showBorder }
      )}
    >
      {renderLabel()}
      {options.sort(sortOrder).map((option, index) => (
        <RadioButton
          key={`radio-button-${index + 1}`}
          disabled={disabled}
          direction={radioLabelDirection}
          label={option.label}
          onChange={onChange}
          selection={selection}
          value={option.value}
        />
      ))}
    </div>
  );
};

const useStyles = () => ({
  container: {
    default: 'relative flex gap-6 items-center flex-wrap',
    direction: {
      row: 'flex-row',
      column: 'flex-col',
    },
    justify: {
      evenly: 'justify-evenly',
      between: 'justify-between',
      center: 'justify-center',
      start: 'justify-start',
      end: 'justify-end',
    },
    padding: {
      none: 'p-0',
      sm: 'p-2',
      md: 'p-5',
      lg: 'p-6',
    },
  },
  width: {
    fullWidth: 'w-full',
    unset: 'w-unset',
  },
  label: {
    default: 'text-md opacity-70',
    group: 'absolute -top-[10px] left-2 px-2 bg-card-light dark:bg-card-dark ',
  },
  showBorder: 'border border-border-light dark:border-border-dark rounded',
});
